import React, { Fragment, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import I18n from "i18n-js";

import api from "../../../../api";

import newPlanSchema from "../../../config/validation/new-plan";

import { copyExistingPlan } from "../../../../services/development-plan";

import InternalModal from "../../../../commons/InternalModal";
import DateInput from "../../../../commons/DateInput";

import FormSection from "./FormSection";
import Options from "./Options";
import CopyItemModal from "../CopyItemModal";

const defaultValues = {
  start_date: null,
  target_date: null,
  option: null
};

const CreatePlanModal = ({ team, show, onHide }) => {

  const schema = newPlanSchema(I18n.t("pdp.form"));
  const form = useForm({ defaultValues, resolver: yupResolver(schema) });
  const { control, handleSubmit, getValues, formState: { errors }} = form;

  const [loading, setLoading] = useState(false);
  const [showPlanPicker, setShowPlanPicker] = useState(false);

  const createPlan = (existingPlan = {}) => {
    const template = getTemplate(existingPlan);
    redirectToNewPlanPage(template);
  }

  const selectDocumentAndGeneratePlan = async (data) => {
    const document = await showDocumentChooser();
    generatePlan(document, data);
  }

  const showDocumentChooser = async () => {
    return new Promise((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".pdf,.doc,.docx,.docm,.xls,.xlsx,.csv,.md";
  
      input.onchange = event => {
        const file = event.target.files[0];

        if (file) {
          resolve(file);
        } else {
          reject(new Error("No file selected!"))
        }
      }
  
      input.click();
    });
  }

  const generatePlan = (document, formData) => {
    if (!document) return;

    setLoading(true);

    const data = new FormData();
    data.append("team_uuid", team?.uuid);
    data.append("document", document);
    data.append("start_date", formData?.start_date);
    data.append("target_date", formData?.target_date);

    api.post("/development_plans/generate", data, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }).then(res => {
        const { template } = res?.data;
        
        if (template) {
          redirectToNewPlanPage(template)
        }
      })
      .catch(err => {
        if (err.response && err.response.data && err.response.data.message) {
          toast.error(<b>{err.response.data.message}</b>);
        } else {
          toast.error(<b>{I18n.t("common.errors.unexpected_error")}</b>);
        }
        setLoading(false);
        console.error(err);
      });
  }

  const showExistingPlanPicker = () => {
    onHide && onHide();
    setShowPlanPicker(true);
  }

  const getTemplate = (plan = {}) => {
    const data = getValues();
    const existingPlan = copyExistingPlan(plan);
    
    return {
      ...existingPlan,
      start_date: data?.start_date,
      target_date: data?.target_date
    };
  }

  const redirectToNewPlanPage = (template) => {
    const encoded = encodeURIComponent(JSON.stringify(template));
    window.location = `/pdp/new?template=${encoded}&locale=${I18n.locale}`;
  }

  const onSubmit = async (data) => {
    switch (data?.option) {
      case "template":
        showExistingPlanPicker();
        break;
      case "generate":
        await selectDocumentAndGeneratePlan(data);
        break;
      default:
        createPlan();
        break;
    }
  }

  const renderModal = () => {
    return (
      <div className="pdp-create-plan-modal">
        <div className="title">
          {I18n.t("pdp.modals.create_plan.title")}
        </div>
        <div className="subtitle">
          {I18n.t("pdp.modals.create_plan.subtitle")}
        </div>
        <FormProvider {...form} defaultValues={defaultValues}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormSection title={I18n.t("pdp.form.timeframe.title")}>
              <div className="pdp-form-row">
                <Controller
                  name="start_date"
                  control={control}
                  disabled={loading}
                  render={({ field: { ref, ...props } }) => (
                    <DateInput
                      placeholder={I18n.t("pdp.form.timeframe.fields.start_date.name")}
                      error={errors.start_date?.message}
                      inputRef={ref}
                      {...props}
                    />
                  )}
                />
                <Controller
                  name="target_date"
                  control={control}
                  disabled={loading}
                  render={({ field: { ref, ...props } }) => (
                    <DateInput
                      placeholder={I18n.t("pdp.form.timeframe.fields.target_date.name")}
                      error={errors.target_date?.message}
                      inputRef={ref}
                      {...props}
                    />
                  )}
                />
              </div>
            </FormSection>
            <Options loading={loading} />
          </form>
        </FormProvider>
      </div>
    );
  }

  return (
    <Fragment>
      <InternalModal
        type="create-plan"
        size="lg"
        isModalOpen={show}
        component={renderModal()}
        showModalBody={false}
        showCloseAction
        hideModal={onHide}
      />
      <CopyItemModal
        show={showPlanPicker}
        type="plan"
        team={team}
        onSelect={createPlan}
        onCancel={() => setShowPlanPicker(false)}
      />
    </Fragment>
  );
}

export default CreatePlanModal;
